// Application configuration

// Environment
export const APP_ENV = import.meta.env.VITE_ENV || 'development'
export const IS_PRODUCTION = APP_ENV === 'production'
export const IS_DEVELOPMENT = APP_ENV === 'development'

// Application info
export const APP_NAME = 'SENOVATE AI - Cattle Farm Management'
export const APP_SHORT_NAME = 'Cattle Farm'
export const APP_VERSION = '1.0.0'
export const APP_DESCRIPTION = 'ระบบจัดการฟาร์มโคเนื้อ'

// Pagination
export const DEFAULT_PAGE_SIZE = 10
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

// File upload
export const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

// LocalStorage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  IS_LOGGED_IN: 'isLoggedIn',
  THEME: 'theme',
  LANGUAGE: 'language',
  SIDEBAR_COLLAPSED: 'sidebar_collapsed',
} as const

// Date formats
export const DATE_FORMATS = {
  DISPLAY: 'dd/MM/yyyy',
  DISPLAY_WITH_TIME: 'dd/MM/yyyy HH:mm',
  API: 'yyyy-MM-dd',
  API_WITH_TIME: "yyyy-MM-dd'T'HH:mm:ss",
  MONTH_YEAR: 'MMMM yyyy',
  SHORT: 'dd MMM yyyy',
} as const

// Toast notification duration
export const TOAST_DURATION = {
  SUCCESS: 3000,
  ERROR: 5000,
  WARNING: 4000,
  INFO: 3000,
} as const

// Debounce delay for search
export const SEARCH_DEBOUNCE_DELAY = 500 // milliseconds

// Sidebar width
export const SIDEBAR_WIDTH = {
  COLLAPSED: 107,
  EXPANDED: 522,
} as const

// Thai provinces
export const THAI_PROVINCES = [
  'กรุงเทพมหานคร',
  'กระบี่',
  'กาญจนบุรี',
  'กาฬสินธุ์',
  'กำแพงเพชร',
  'ขอนแก่น',
  'จันทบุรี',
  'ฉะเชิงเทรา',
  'ชลบุรี',
  'ชัยนาท',
  'ชัยภูมิ',
  'ชุมพร',
  'เชียงราย',
  'เชียงใหม่',
  'ตรัง',
  'ตราด',
  'ตาก',
  'นครนายก',
  'นครปฐม',
  'นครพนม',
  'นครราชสีมา',
  'นครศรีธรรมราช',
  'นครสวรรค์',
  'นนทบุรี',
  'นราธิวาส',
  'น่าน',
  'บึงกาฬ',
  'บุรีรัมย์',
  'ปทุมธานี',
  'ประจวบคีรีขันธ์',
  'ปราจีนบุรี',
  'ปัตตานี',
  'พระนครศรีอยุธยา',
  'พังงา',
  'พัทลุง',
  'พิจิตร',
  'พิษณุโลก',
  'เพชรบุรี',
  'เพชรบูรณ์',
  'แพร่',
  'พะเยา',
  'ภูเก็ต',
  'มหาสารคาม',
  'มุกดาหาร',
  'แม่ฮ่องสอน',
  'ยะลา',
  'ยโสธร',
  'ร้อยเอ็ด',
  'ระนอง',
  'ระยอง',
  'ราชบุรี',
  'ลพบุรี',
  'ลำปาง',
  'ลำพูน',
  'เลย',
  'ศรีสะเกษ',
  'สกลนคร',
  'สงขลา',
  'สตูล',
  'สมุทรปราการ',
  'สมุทรสงคราม',
  'สมุทรสาคร',
  'สระแก้ว',
  'สระบุรี',
  'สิงห์บุรี',
  'สุโขทัย',
  'สุพรรณบุรี',
  'สุราษฎร์ธานี',
  'สุรินทร์',
  'หนองคาย',
  'หนองบัวลำภู',
  'อ่างทอง',
  'อำนาจเจริญ',
  'อุดรธานี',
  'อุตรดิตถ์',
  'อุทัยธานี',
  'อุบลราชธานี',
] as const

// Cow breeds (Thai labels)
export const COW_BREEDS: Record<string, string> = {
  // Lowercase (for form inputs)
  brahman: 'พราหมัน',
  charolais: 'ชาร์โรเลส์',
  angus: 'แองกัส',
  simmental: 'ซิมเมนทัล',
  mixed: 'ลูกผสม',
  other: 'อื่นๆ',
  // Uppercase (from backend)
  BRAHMAN: 'พราหมัน',
  CHAROLAIS: 'ชาร์โรเลส์',
  ANGUS: 'แองกัส',
  SIMMENTAL: 'ซิมเมนทัล',
  MIXED: 'ลูกผสม',
  OTHER: 'อื่นๆ',
}

// Cattle types (Thai labels) - กลุ่มวัว
export const CATTLE_TYPES: Record<string, string> = {
  BREEDING_BULL: 'พ่อพันธุ์',
  BREEDING_COW: 'แม่พันธุ์',
  CALF: 'ลูกโค',
  YOUNG_CATTLE: 'โครุ่น',
  FATTENING_CATTLE: 'โคขุน',
}

// Transaction categories (Thai labels)
export const INCOME_CATEGORIES = {
  sale: 'ขายโค',
  breeding_fee: 'ค่าผสมพันธุ์',
  subsidy: 'เงินอุดหนุน',
  other_income: 'รายได้อื่นๆ',
} as const

export const EXPENSE_CATEGORIES = {
  feed: 'อาหารสัตว์',
  medicine: 'ยา/วัคซีน',
  equipment: 'อุปกรณ์',
  labor: 'ค่าแรง',
  utilities: 'ค่าสาธารณูปโภค',
  maintenance: 'ค่าซ่อมบำรุง',
  purchase: 'ซื้อโค',
  veterinary: 'ค่าสัตวแพทย์',
  transportation: 'ค่าขนส่ง',
  other_expense: 'ค่าใช้จ่ายอื่นๆ',
} as const

// Payment methods (Thai labels)
export const PAYMENT_METHODS = {
  cash: 'เงินสด',
  bank_transfer: 'โอนเงิน',
  credit_card: 'บัตรเครดิต',
  promissory_note: 'เงินเชื่อ',
  other: 'อื่นๆ',
} as const

// Behavior types (Thai labels)
export const BEHAVIOR_TYPES = {
  movement: 'การเคลื่อนไหว',
  feeding: 'การกินอาหาร',
  resting: 'การพักผ่อน',
  standing: 'การยืน',
  walking: 'การเดิน',
  running: 'การวิ่ง',
  mounting: 'การขี่ขวาง',
  estrus_behavior: 'พฤติกรรมเป็นสัด',
} as const

// Activity levels (Thai labels)
export const ACTIVITY_LEVELS = {
  very_low: 'ต่ำมาก',
  low: 'ต่ำ',
  normal: 'ปกติ',
  high: 'สูง',
  very_high: 'สูงมาก',
} as const

// Estrus severity (Thai labels)
export const ESTRUS_SEVERITY = {
  none: 'ไม่มี',
  mild: 'เล็กน้อย',
  moderate: 'ปานกลาง',
  strong: 'มาก',
  confirmed: 'ยืนยัน',
} as const

// Notification types (Thai labels)
export const NOTIFICATION_TYPES = {
  estrus_detected: 'ตรวจพบการเป็นสัด',
  behavior_change: 'พฤติกรรมเปลี่ยนแปลง',
  health_alert: 'เตือนสุขภาพ',
  low_activity: 'กิจกรรมต่ำ',
  high_activity: 'กิจกรรมสูง',
  temperature_alert: 'อุณหภูมิผิดปกติ',
  feeding_alert: 'เตือนการกินอาหาร',
  general: 'ทั่วไป',
} as const

// Notification priority (Thai labels)
export const NOTIFICATION_PRIORITY = {
  low: 'ต่ำ',
  medium: 'ปานกลาง',
  high: 'สูง',
  critical: 'วิกฤต',
} as const

// Farmer experience levels (Thai labels)
export const FARMER_EXPERIENCE_LEVELS = {
  beginner: 'เริ่มต้น',
  intermediate: 'ปานกลาง',
  advanced: 'ชำนาญ',
  expert: 'ผู้เชี่ยวชาญ',
} as const

// Farming types (Thai labels)
export const FARMING_TYPES = {
  beef: 'โคเนื้อ',
  dairy: 'โคนม',
  mixed: 'ผสม',
  breeding: 'พ่อพันธุ์แม่พันธุ์',
} as const

// Behavior tracking settings
export const BEHAVIOR_SETTINGS = {
  // Estrus detection thresholds
  ESTRUS_SCORE_THRESHOLD: 70, // 0-100
  ESTRUS_ACTIVITY_INCREASE: 30, // percentage increase
  ESTRUS_TEMPERATURE_INCREASE: 0.5, // celsius

  // Activity monitoring
  LOW_ACTIVITY_THRESHOLD: 30, // percentage of normal
  HIGH_ACTIVITY_THRESHOLD: 150, // percentage of normal

  // Data collection intervals
  BEHAVIOR_RECORD_INTERVAL: 15, // minutes
  TEMPERATURE_CHECK_INTERVAL: 60, // minutes
  SUMMARY_GENERATION_INTERVAL: 1440, // minutes (daily)

  // Alert cooldowns
  DEFAULT_ALERT_COOLDOWN: 60, // minutes
  ESTRUS_ALERT_COOLDOWN: 720, // minutes (12 hours)
  HEALTH_ALERT_COOLDOWN: 30, // minutes
} as const
